import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import imageLogo from "../../../assets/orderingSystem/login-bg.jpg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import {
  customerLogin,
  customerPhoneLogin,
  setPhoneForLoginOtp,
} from "../redux/slices/customerAuthSlice";
import PublicOrderingServices from "../services/PublicOrderingServices";
import { clearCart } from "../../../slices/cart/publicOrderingCartSlice";
import toast from "react-hot-toast";

const arrow =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Cpath d='M10 25 Q50 5 80 25' stroke='white' fill='none' stroke-width='3' stroke-linecap='round'/%3E%3Cpolygon points='80,25 70,15 75,25 70,35' fill='white'/%3E%3C/svg%3E";

const CustomerLogin = () => {
  const [passwordShow, setPasswordShow] = useState(false);
  const [showUserSheet, setShowUserSheet] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [loginType, setLoginType] = useState("phone"); // "phone" or "email"
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgcode } = useParams();
  const location = useLocation();
  console.log("🚀 ~ CustomerLogin ~ orgcode:", orgcode);
  const { loading, error } = useSelector((s) => s.customerAuth || {});

  const [savedUsers, setSavedUsers] = useState(() => {
    try {
      const storedUsers = localStorage.getItem("savedCustomers");
      if (!storedUsers) return [];
      const parsedUsers = JSON.parse(storedUsers);
      const uniqueUsers = parsedUsers.filter(
        (v, i, a) => a.findIndex((u) => u.user.email === v.user.email) === i
      );
      return uniqueUsers;
    } catch {
      return [];
    }
  });

  const toggleUserSheet = () => setShowUserSheet(!showUserSheet);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      password: "",
      phoneNumber: "",
    },
    validationSchema: Yup.object({
      username:
        loginType === "email"
          ? Yup.string()
              .email("Please enter a valid email")
              .required("Please enter your email")
          : Yup.string(),
      password:
        loginType === "email"
          ? Yup.string().required("Please enter your password")
          : Yup.string(),
      phoneNumber:
        loginType === "phone"
          ? Yup.string()
              .matches(
                /^[0-9]{10,}$/,
                "Phone number must be at least 10 digits"
              )
              .required("Please enter your phone number")
          : Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        setErrorMsg(null);

        if (loginType === "phone") {
          // Phone login - send OTP
          const res = await dispatch(customerPhoneLogin(values.phoneNumber));
          if (res?.type?.includes("fulfilled")) {
            dispatch(setPhoneForLoginOtp(values.phoneNumber));
            navigate(`/${orgcode}/verify-otp`);
          } else {
            const errorMsg =
              res?.payload?.message ||
              res?.payload ||
              "Phone login failed. Please try again.";
            setErrorMsg(errorMsg);
          }
        } else {
          // Email login - direct login without OTP
          const res = await dispatch(customerLogin(values));

          if (res?.type?.includes("fulfilled")) {
            // Save the full login response to localStorage
            const loginResponse = res?.payload;
            localStorage.setItem("CustomerInfo", JSON.stringify(loginResponse));

            // Store quick login entry
            const newUser = {
              user: {
                email: values.username,
                roles: ["Customer"],
              },
            };
            const userExists = savedUsers.some(
              (item) => item.user.email === values.username
            );
            if (!userExists) {
              const updatedUsers = [...savedUsers, newUser];
              setSavedUsers(updatedUsers);
              localStorage.setItem(
                "savedCustomers",
                JSON.stringify(updatedUsers)
              );
            }

            // After successful login, check for any pending checkout stored in localStorage
            try {
              const pendingRaw =
                location.state?.orderPayload ||
                localStorage.getItem("pendingCheckout");
              const pending = pendingRaw
                ? typeof pendingRaw === "string"
                  ? JSON.parse(pendingRaw)
                  : pendingRaw
                : null;

              if (pending && pending.orderPayload) {
                try {
                  const response = await PublicOrderingServices.orderOnline({
                    token: loginResponse?.accessToken,
                    apikey: pending.orgData?.orgCode || orgcode,
                    orderPayload: pending.orderPayload,
                  });

                  // clear client cart and pending flag
                  try {
                    localStorage.removeItem("publicCartV2");
                  } catch (e) {}
                  dispatch(clearCart());
                  localStorage.removeItem("pendingCheckout");

                  // Navigate to order summary with the placed order response
                  navigate(`/${orgcode}/order-summary`, {
                    state: { orgData: pending.orgData, orderPost: response, orderResponse: response },
                  });
                  return;
                } catch (err) {
                  console.error("Error placing pending checkout after login:", err);
                  // fallback: continue to default navigation
                }
              }
            } catch (err) {
              console.error("Error processing pending checkout after login:", err);
            }

            navigate(`/${orgcode}`);
          } else {
            // show error from payload or generic
            const errorMsg =
              res?.payload?.message ||
              res?.payload ||
              "Login failed. Please try again.";
            setErrorMsg(errorMsg);
          }
        }
      } catch (err) {
        setErrorMsg("Login failed. Please try again.");
        console.error("Login failed:", err);
      }
    },
  });

  const handleCardClick = (email) => {
    validation.setFieldValue("username", email);
  };

  const handleDeleteUser = (email) => {
    const updatedUsers = savedUsers.filter(
      (item) => item?.user?.email !== email
    );
    setSavedUsers(updatedUsers);
    localStorage.setItem("savedCustomers", JSON.stringify(updatedUsers));
  };

  useEffect(() => {
    if (errorMsg) {
      const timer = setTimeout(() => {
        setErrorMsg(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMsg]);

  document.title = "Customer Login - Ordering System";

  return (
    <React.Fragment>
      <div className="vh-100">
        <Row className="h-100 g-0">
          {/* Login Form - Large screens */}
          <Col
            lg={{ size: 5, order: 2 }}
            md={{ size: 5, order: 2 }}
            xs={{ size: 12, order: 1 }}
            sm={{ size: 12, order: 1 }}
            xl={{ size: 6, order: 2 }}
            className="h-100 d-none d-sm-none d-md-block d-lg-block"
            style={{ backgroundColor: "#0f403b" }}
          >
            <div className="py-5 d-flex flex-column justify-content-center align-items-center h-100">
              <CardBody className="p-4">
                <div className="text-center mb-lg-4 mb-1">
                  <h5 className="text-white">
                    Sign in to your account to place orders
                  </h5>
                </div>

                {errorMsg &&
                  // <Alert color="danger">
                  //   {typeof errorMsg === "string"
                  //     ? errorMsg
                  //     : errorMsg?.message || JSON.stringify(errorMsg)}
                  // </Alert>
                  toast.error("Incorrect Password")}

                <div className="p-2 py-4">
                  {/* Login Type Toggle */}
                  <div className="d-flex justify-content-center mb-4">
                    <div className="btn-group" role="group">
                      <Button
                        type="button"
                        color={loginType === "phone" ? "success" : "secondary"}
                        onClick={() => setLoginType("phone")}
                        className="btn-sm d-flex align-items-center"
                      >
                        <i className="ri-phone-line me-1"></i>
                        Phone Login
                      </Button>
                      <Button
                        type="button"
                        color={loginType === "email" ? "success" : "secondary"}
                        onClick={() => setLoginType("email")}
                        className="btn-sm d-flex align-items-center"
                      >
                        <i className="ri-mail-line me-1"></i>
                        Email Login
                      </Button>
                    </div>
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    {loginType === "phone" ? (
                      <div className="mb-3">
                        <Input
                          name="phoneNumber"
                          className="form-control p-3"
                          placeholder="Enter phone number"
                          type="tel"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.phoneNumber || ""}
                          invalid={
                            validation.touched.phoneNumber &&
                            validation.errors.phoneNumber
                              ? true
                              : false
                          }
                        />
                        {validation.touched.phoneNumber &&
                        validation.errors.phoneNumber ? (
                          <FormFeedback type="invalid">
                            {validation.errors.phoneNumber}
                          </FormFeedback>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <div className="mb-3">
                          <Input
                            name="username"
                            className="form-control p-3"
                            placeholder="Enter email"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.username || ""}
                            invalid={
                              validation.touched.username &&
                              validation.errors.username
                                ? true
                                : false
                            }
                          />
                          {validation.touched.username &&
                          validation.errors.username ? (
                            <FormFeedback type="invalid">
                              {validation.errors.username}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3 mt-1">
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              value={validation.values.password || ""}
                              type={passwordShow ? "text" : "password"}
                              className="form-control pe-5 p-3"
                              placeholder="Enter Password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.password &&
                                validation.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 pt-3 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="d-flex justify-content-between mb-4">
                      {loginType === "email" && (
                        <>
                          <div className="form-check">
                            <Input
                              className="form-check-input"
                              type="checkbox"
                              value=""
                              id="auth-remember-check"
                            />
                            <Label
                              className="form-check-label text-white mt-0"
                              htmlFor="auth-remember-check"
                            >
                              Remember me
                            </Label>
                          </div>

                          <div className="float-end">
                            <Link
                              to={`/${orgcode}/customer-forgot-password`}
                              className="text-white"
                            >
                              Forgot password?
                            </Link>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="mb-3">
                      <Button
                        disabled={loading}
                        color="success"
                        className="btn btn-success w-100"
                        type="submit"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {loginType === "phone" ? "Sending OTP..." : "Logging in..."}
                          </>
                        ) : (
                          loginType === "phone" ? "Send OTP" : "Login"
                        )}
                      </Button>
                    </div>
                    <Col xs={12} lg={12} className="d-lg-block d-none">
                      <div className="text-center text-white">
                        Don't have an account?{" "}
                        <Link
                          to={`/${orgcode}/customer-register`}
                          className="text-warning"
                        >
                          Sign up
                        </Link>
                      </div>
                    </Col>
                  </form>
                </div>
              </CardBody>

              <footer className="mt-auto w-100">
                <div className="container">
                  <Row>
                    <Col xs={12}>
                      <div className="text-center">
                        <p className="mb-0 text-muted">
                          &copy; {new Date().getFullYear()} Danfe Solution
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </footer>
            </div>
          </Col>

          {/* Login Form - Small screens */}
          <Col
            lg={{ size: 5, order: 2 }}
            xs={{ size: 12, order: 1 }}
            sm={{ size: 12, order: 1 }}
            xl={{ size: 6, order: 2 }}
            className="h-100 d-block d-sm-block d-md-none d-lg-none position-relative"
          >
            <img
              src={imageLogo}
              alt="Public Ordering System"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
            <div
              className="py-5 d-flex flex-column justify-content-center align-items-center h-100"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CardBody className="p-2 d-block justify-content-center align-content-center align-items-center w-100">
                <div className="text-center">
                  <h3
                    style={{ color: "white" }}
                    className="fw-bold mb-4 d-lg-none d-block"
                  >
                    Public Ordering System
                    <img src={arrow} alt="" className="w-25 ms-2" />
                  </h3>
                  <h6 className="text-light">
                    Sign in to your account to place orders
                  </h6>
                </div>

                {errorMsg && toast.error("Incorrect Password")}

                <div className="p-2 py-3">
                  {/* Login Type Toggle */}
                  <div className="d-flex justify-content-center mb-4">
                    <div className="btn-group" role="group">
                      <Button
                        type="button"
                        color={loginType === "phone" ? "success" : "secondary"}
                        onClick={() => setLoginType("phone")}
                        className="btn-sm"
                      >
                        <i className="ri-phone-line me-1"></i>
                        Phone Login
                      </Button>
                      <Button
                        type="button"
                        color={loginType === "email" ? "success" : "secondary"}
                        onClick={() => setLoginType("email")}
                        className="btn-sm"
                      >
                        <i className="ri-mail-line me-1"></i>
                        Email Login
                      </Button>
                    </div>
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    {loginType === "phone" ? (
                      <div className="mb-3">
                        <Input
                          name="phoneNumber"
                          className="form-control p-3"
                          placeholder="Enter phone number"
                          type="tel"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.phoneNumber || ""}
                          invalid={
                            validation.touched.phoneNumber &&
                            validation.errors.phoneNumber
                              ? true
                              : false
                          }
                        />
                        {validation.touched.phoneNumber &&
                        validation.errors.phoneNumber ? (
                          <FormFeedback type="invalid">
                            {validation.errors.phoneNumber}
                          </FormFeedback>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <div className="mb-3">
                          <Input
                            name="username"
                            className="form-control p-3"
                            placeholder="Enter email"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.username || ""}
                            invalid={
                              validation.touched.username &&
                              validation.errors.username
                                ? true
                                : false
                            }
                          />
                          {validation.touched.username &&
                          validation.errors.username ? (
                            <FormFeedback type="invalid">
                              {validation.errors.username}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3 mt-1">
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              value={validation.values.password || ""}
                              type={passwordShow ? "text" : "password"}
                              className="form-control pe-5 p-3"
                              placeholder="Enter Password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.password &&
                                validation.errors.password
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 pt-3 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    <Row className="d-flex justify-content-between mb-4">
                      {loginType === "email" && (
                        <>
                          <Col xs={12} lg={6}>
                            <div className="form-check">
                              <Input
                                className="form-check-input"
                                type="checkbox"
                                value=""
                                id="auth-remember-check"
                              />
                              <Label
                                className="form-check-label text-white mt-0"
                                htmlFor="auth-remember-check"
                              >
                                Remember me
                              </Label>
                            </div>
                          </Col>

                          <Col xs={12} lg={6} className="d-lg-block d-none">
                            <div className="float-end">
                              <Link
                                to={`/${orgcode}/customer-forgot-password`}
                                className="text-white"
                              >
                                Forgot password?
                              </Link>
                            </div>
                          </Col>
                        </>
                      )}
                    </Row>

                    <div className="mb-3">
                      <Button
                        disabled={loading}
                        color="success"
                        className="btn btn-success w-100"
                        type="submit"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {loginType === "phone" ? "Sending OTP..." : "Logging in..."}
                          </>
                        ) : (
                          loginType === "phone" ? "Send OTP" : "Login"
                        )}
                      </Button>
                    </div>

                    {loginType === "email" && (
                      <Col xs={12} lg={6} className="d-lg-none d-block mb-4">
                        <div className="text-center">
                          <Link
                            to={`/${orgcode}/customer-forgot-password`}
                            className="text-white"
                          >
                            Forgot password?
                          </Link>
                        </div>
                      </Col>
                    )}
                    <Col xs={12} lg={6} className="d-lg-none d-block">
                      <div className="text-center text-white">
                        Don't have an account?{" "}
                        <Link
                          to={`/${orgcode}/customer-register`}
                          className="text-warning"
                        >
                          Sign up
                        </Link>
                      </div>
                    </Col>
                  </form>
                </div>
              </CardBody>

              <footer className="mt-auto w-100">
                <div className="container">
                  <Row>
                    <Col xs={12}>
                      <div className="text-center">
                        <p className="mb-0 text-muted">
                          &copy; {new Date().getFullYear()} Danfe Solution
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </footer>
            </div>
          </Col>

          {/* Image Part with User Cards */}
          <Col
            lg={{ size: 7, order: 1 }}
            md={{ size: 7, order: 1 }}
            xl={{ size: 6, order: 1 }}
            xs={{ size: 12, order: 2 }}
            sm={{ size: 12, order: 2 }}
            className="position-relative h-100 d-none d-sm-none d-md-flex d-lg-flex"
          >
            <img
              src={imageLogo}
              alt="Background"
              className="w-100 h-100"
              style={{ objectFit: "cover" }}
            />

            <div className="login-overlay d-flex flex-column justify-content-center align-items-start position-absolute w-100 h-100">
              <div className="overlay-content w-100 px-4">
                <h1 className="fw-bold display-5 mb-4 text-white d-flex align-items-center">
                  Public Ordering System
                  <img
                    src={arrow}
                    alt=""
                    className="ms-2"
                    style={{ height: "40px" }}
                  />
                </h1>
                {/* <div>
                  <Swiper
                    modules={[Navigation, Pagination]}
                    spaceBetween={20}
                    slidesPerView={1}
                    breakpoints={{
                      576: { slidesPerView: 2 },
                      768: { slidesPerView: 2 },
                      992: { slidesPerView: 2 },
                      1200: { slidesPerView: 2 },
                    }}
                    loop={savedUsers.length > 0}
                    className="py-3"
                    navigation
                  >
                    {savedUsers && savedUsers.length > 0 ? (
                      savedUsers.map((item, index) => (
                        <SwiperSlide key={index}>
                          <Card
                            onClick={() => handleCardClick(item?.user?.email)}
                            className="user-card text-center p-2 border-0 h-100 shadow-sm"
                            style={{ cursor: "pointer" }}
                          >
                            <Row className="align-items-center g-2">
                              <Col
                                xs={3}
                                className="d-flex justify-content-center"
                              >
                                <div
                                  className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center"
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    fontSize: "1.1rem",
                                    fontWeight: "700",
                                  }}
                                >
                                  {item?.user?.email
                                    ?.substring(0, 2)
                                    .toUpperCase()}
                                </div>
                              </Col>

                              <Col xs={9} className="text-start">
                                <h6 className="mb-1 text-dark fw-bold text-truncate text-start">
                                  {item?.user?.email}
                                </h6>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="badge rounded-pill bg-light text-dark shadow-sm">
                                    {item?.user?.roles[0]}
                                  </span>
                                  <button
                                    className="btn btn-sm p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteUser(item?.user?.email);
                                    }}
                                    type="button"
                                  >
                                    <i className="ri-delete-bin-line text-danger"></i>
                                  </button>
                                </div>
                              </Col>
                            </Row>
                          </Card>
                        </SwiperSlide>
                      ))
                    ) : (
                      <SwiperSlide>
                        <div className="text-center text-white py-5">
                          <p>No saved customers</p>
                        </div>
                      </SwiperSlide>
                    )}
                  </Swiper>
                </div> */}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Mobile Saved Users Sheet Toggle */}
      <div
        className="d-sm-block d-md-none position-fixed bottom-0 start-0 w-100 text-center mb-2"
        style={{ zIndex: 1050 }}
        onClick={toggleUserSheet}
      >
        <div
          style={{
            width: "100px",
            height: "6px",
            background: "#bbb",
            borderRadius: "5px",
            margin: "0 auto",
          }}
        ></div>
      </div>

      <style>{`
        .login-overlay {
          top: 0;
          left: 0;
        }

        .user-card {
          transition: all 0.3s ease;
          background-color: #f8f9fa;
        }

        .user-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .auth-pass-inputgroup input {
          border-radius: 8px;
        }

        .form-control {
          border-radius: 8px;
          border: 1px solid #ddd;
        }

        .form-control:focus {
          border-color: #28a745;
          box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
        }

        .btn-success {
          background-color: #28a745;
          border-color: #28a745;
          border-radius: 8px;
          font-weight: 600;
        }

        .btn-success:hover {
          background-color: #218838;
          border-color: #218838;
        }

        .btn-success:disabled {
          background-color: #ccc;
          border-color: #ccc;
        }

        @media (max-width: 768px) {
          .vh-100 {
            min-height: 100vh;
          }
        }
      `}</style>
    </React.Fragment>
  );
};

export default CustomerLogin;
